import React, { useState, useEffect, useContext } from "react";
import {
  Box,
  CssBaseline,
  Toolbar,
  Drawer,
  useTheme,
} from "@mui/material";
import { useLocation, useNavigate } from "react-router-dom";

import Header from "./shared/components/header";
import SidebarContent from "./shared/components/sidebar";
import routes from "./config/routes";
import { AuthContext } from "./auth/authContext";
import { DRAWER_WIDTH } from "./shared/constants/constants";

import useResponsive from "./shared/hooks/useResponsive";



const Layout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const { logout } = useContext(AuthContext);

  const { isMobile, isTablet, isDesktop } = useResponsive();

  const isLoginPage = location.pathname === routes.LOGIN;

  /** 
   * Gestion dynamique du sidebar :
   * - Mobile : fermé par défaut
   * - Tablet & Desktop : ouvert par défaut
   */
  const [sidebarOpen, setSidebarOpen] = useState(isDesktop || isTablet);

  // si on passe de desktop → mobile, auto-fermer pour éviter le bug du drawer ouvert
  useEffect(() => {
    if (isMobile) setSidebarOpen(false);
    else setSidebarOpen(true);
  }, [isMobile]);

  const toggleSidebar = () => setSidebarOpen((prev) => !prev);

  const handleLogout = () => {
    logout(); // Utilise logout() du AuthContext — supprime "access_token" ET vide le state user
    navigate(routes.LOGIN, { replace: true });
  };

  return (
    <Box sx={{ display: "flex" }}>
      <CssBaseline />

      {!isLoginPage && (
        <>
          {/* HEADER */}
          <Header onToggleSidebar={toggleSidebar} onLogout={handleLogout} />

          {/* SIDEBAR DESKTOP / TABLET */}
          {!isMobile && (
            <Box
              sx={{
                width: sidebarOpen ? DRAWER_WIDTH : 0,
                flexShrink: 0,
                transition: theme.transitions.create("width", {
                  easing: theme.transitions.easing.sharp,
                  duration: theme.transitions.duration.enteringScreen,
                }),
                overflow: "hidden",
              }}
            >
              <SidebarContent open={sidebarOpen} onClose={toggleSidebar} />
            </Box>
          )}

          {/* SIDEBAR MOBILE (Drawer) */}
          {isMobile && (
            <Drawer
              variant="temporary"
              open={sidebarOpen}
              onClose={toggleSidebar}
              ModalProps={{ keepMounted: true }}
              sx={{
                "& .MuiDrawer-paper": {
                  width: DRAWER_WIDTH,
                },
              }}
            >
              <SidebarContent open={sidebarOpen} onClose={toggleSidebar} />
            </Drawer>
          )}
        </>
      )}

      {/* MAIN CONTENT */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          transition: theme.transitions.create(["margin", "padding"], {
            easing: theme.transitions.easing.sharp,
            duration: theme.transitions.duration.enteringScreen,
          }),

          /** Padding responsive */
          p: isMobile ? 1.5 : isTablet ? 2.5 : 3.5,

          /** Décalage du main si sidebar ouvert */
          marginLeft: 0,
        }}
      >
        {!isLoginPage && <Toolbar />}
        {children}
      </Box>
    </Box>
  );
};

export default Layout;
